import { useState, useEffect } from "react";
import api from "../lib/api";
import { QUIZ_CATEGORIES, QUIZ_QUESTIONS, QUIZ_FLAT, VERDICT_META } from "../data/vaQuiz";
import { ClipboardCheck, ChevronRight, ChevronLeft, Check, Trash2, Award, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VARecruiting() {
  const [tab, setTab] = useState("quiz");
  return (
    <div className="space-y-8 animate-fadeUp" data-testid="va-recruiting-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// RECRUTEMENT VA</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Quiz Virtual Assistants</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">22 questions sur 8 catégories. Scoring auto sur 100. Verdict instantané : <span className="text-[#10B981]">Elite</span> · <span className="text-[#CCFF00]">Fort potentiel</span> · <span className="text-[#F59E0B]">À former</span> · <span className="text-[#EF4444]">Refus</span>.</p>
      </header>

      <div className="border-b border-[#1E1E24] flex gap-0">
        {[{ id: "quiz", label: "Lancer un quiz", icon: ClipboardCheck }, { id: "candidats", label: "Candidats", icon: Users }].map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`va-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-wider transition border-b-2 ${
                tab === t.id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}>
              <I className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "quiz" ? <QuizFlow /> : <CandidatesList />}
    </div>
  );
}

// ============== QUIZ FLOW ==============
function QuizFlow() {
  const [step, setStep] = useState("identity"); // identity | quiz | result
  const [identity, setIdentity] = useState({ full_name: "", email: "", country: "", timezone: "", notes: "" });
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const total = QUIZ_FLAT.length;
  const current = QUIZ_FLAT[currentIdx];
  const answered = Object.keys(answers).length;
  const allAnswered = answered === total;

  const startQuiz = (e) => {
    e.preventDefault();
    if (!identity.full_name) return toast.error("Nom requis");
    setStep("quiz");
  };

  const answer = (qid, oid) => {
    setAnswers(a => ({ ...a, [qid]: oid }));
    if (currentIdx < total - 1) {
      setTimeout(() => setCurrentIdx(i => i + 1), 200);
    }
  };

  const submit = async () => {
    if (!allAnswered) return toast.error("Réponds à toutes les questions");
    setLoading(true);
    try {
      const r = await api.submitCandidate({ ...identity, answers });
      setResult(r);
      setStep("result");
    } catch { toast.error("Erreur soumission"); }
    finally { setLoading(false); }
  };

  const reset = () => {
    setStep("identity");
    setIdentity({ full_name: "", email: "", country: "", timezone: "", notes: "" });
    setAnswers({});
    setCurrentIdx(0);
    setResult(null);
  };

  if (step === "identity") {
    return (
      <form onSubmit={startQuiz} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 max-w-2xl space-y-4" data-testid="identity-form">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#CCFF00] mb-2">// ÉTAPE 1 / 2 — IDENTITÉ CANDIDAT</div>
          <h2 className="font-display text-2xl font-semibold text-white">Avant de commencer</h2>
          <p className="text-sm text-zinc-400 mt-1">Renseigne le candidat. Le quiz prend ~10 minutes, scoring instantané à la fin.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nom complet *" value={identity.full_name} onChange={v => setIdentity({ ...identity, full_name: v })} required testid="cand-name" />
          <Field label="Email" value={identity.email} onChange={v => setIdentity({ ...identity, email: v })} type="email" testid="cand-email" />
          <Field label="Pays" value={identity.country} onChange={v => setIdentity({ ...identity, country: v })} />
          <Field label="Timezone (ex: GMT+1)" value={identity.timezone} onChange={v => setIdentity({ ...identity, timezone: v })} />
        </div>
        <textarea placeholder="Notes (optionnel)" value={identity.notes} onChange={e => setIdentity({ ...identity, notes: e.target.value })} rows={2}
          className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none" />
        <button type="submit" data-testid="start-quiz-btn"
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          Démarrer le quiz <ChevronRight className="w-4 h-4" />
        </button>
      </form>
    );
  }

  if (step === "result" && result) {
    const meta = VERDICT_META[result.verdict] || {};
    return (
      <div className="space-y-6 animate-fadeUp" data-testid="quiz-result">
        <div className="border-2 rounded-sm p-8 text-center" style={{ borderColor: `${meta.color}60`, background: `${meta.color}08` }}>
          <Award className="w-12 h-12 mx-auto mb-4" style={{ color: meta.color }} strokeWidth={1.2} />
          <div className="font-mono text-7xl font-bold mb-2" style={{ color: meta.color }} data-testid="result-score">{result.score_pct}%</div>
          <div className="font-display text-3xl font-bold uppercase tracking-tight mb-2" style={{ color: meta.color }}>{result.verdict}</div>
          <div className="text-sm text-zinc-400">{meta.desc}</div>
          <div className="text-xs font-mono text-zinc-500 mt-3">{result.score_total} / {result.score_max} points</div>
        </div>

        <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-4">Détail par catégorie</div>
          <div className="space-y-3">
            {QUIZ_CATEGORIES.map(c => {
              const got = result.scores_by_category?.[c.id] || 0;
              const max = (QUIZ_QUESTIONS[c.id] || []).length * 5;
              const pct = max ? Math.round((got / max) * 100) : 0;
              return (
                <div key={c.id}>
                  <div className="flex items-center justify-between text-xs font-mono mb-1">
                    <span className="text-zinc-300 uppercase tracking-wider">{c.label}</span>
                    <span className="text-white">{got}/{max} <span className="text-zinc-500">· {pct}%</span></span>
                  </div>
                  <div className="h-1.5 bg-[#1E1E24] rounded-full overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: c.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button onClick={reset} data-testid="reset-quiz-btn"
          className="border border-[#1E1E24] text-zinc-400 hover:text-white px-5 py-2.5 rounded-sm text-sm">
          Lancer un nouveau quiz →
        </button>
      </div>
    );
  }

  // step === "quiz"
  const cat = QUIZ_CATEGORIES.find(c => c.id === current.category);
  return (
    <div className="space-y-5 max-w-3xl animate-fadeUp">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider">
          <span className="text-[#CCFF00]">Question {currentIdx + 1} / {total}</span>
          <span className="text-zinc-500">{answered} / {total} répondues</span>
        </div>
        <div className="h-1 bg-[#1E1E24] rounded-full overflow-hidden">
          <div className="h-full bg-[#CCFF00] transition-all" style={{ width: `${((currentIdx + 1) / total) * 100}%` }} />
        </div>
      </div>

      {/* Category badge */}
      <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border" style={{ color: cat?.color, borderColor: `${cat?.color}40`, background: `${cat?.color}10` }}>
        {cat?.label}
      </div>

      {/* Question */}
      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 space-y-5">
        <h3 className="font-display text-xl font-semibold text-white leading-tight" data-testid="quiz-question">{current.text}</h3>
        <div className="space-y-2">
          {current.options.map(opt => {
            const selected = answers[current.id] === opt.id;
            return (
              <button key={opt.id} onClick={() => answer(current.id, opt.id)} data-testid={`quiz-opt-${current.id}-${opt.id}`}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-sm border transition-all ${
                  selected
                    ? "border-[#CCFF00] bg-[#CCFF00]/10 text-white"
                    : "border-[#1E1E24] bg-[#0C0C0E] text-zinc-300 hover:border-white/30 hover:bg-[#0E0E11]"
                }`}>
                <span className={`shrink-0 w-5 h-5 rounded-sm flex items-center justify-center mt-0.5 ${selected ? "bg-[#CCFF00] text-black" : "border border-[#1E1E24]"}`}>
                  {selected ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <span className="text-[10px] font-mono uppercase">{opt.id}</span>}
                </span>
                <span className="text-sm leading-relaxed">{opt.text}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between">
        <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0} data-testid="prev-q-btn"
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-4 h-4" /> Précédente
        </button>
        {currentIdx < total - 1 ? (
          <button onClick={() => setCurrentIdx(i => Math.min(total - 1, i + 1))} data-testid="next-q-btn"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            Suivante <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={!allAnswered || loading} data-testid="submit-quiz-btn"
            className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center gap-2 text-sm glow-volt">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
            {loading ? "Calcul du score..." : `Soumettre (${answered}/${total})`}
          </button>
        )}
      </div>
    </div>
  );
}

// ============== CANDIDATES LIST ==============
function CandidatesList() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => setList(await api.listCandidates());
  useEffect(() => { load(); }, []);

  const del = async (id) => { await api.deleteCandidate(id); toast.success("Supprimé"); load(); };

  const filtered = filter === "all" ? list : list.filter(c => c.verdict === filter);
  const sorted = [...filtered].sort((a, b) => b.score_pct - a.score_pct);

  const counts = {
    Elite: list.filter(c => c.verdict === "Elite").length,
    "Fort potentiel": list.filter(c => c.verdict === "Fort potentiel").length,
    "À former": list.filter(c => c.verdict === "À former").length,
    "Refus": list.filter(c => c.verdict === "Refus").length,
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(VERDICT_META).map(([verdict, meta]) => (
          <button key={verdict} onClick={() => setFilter(filter === verdict ? "all" : verdict)} data-testid={`filter-${verdict}`}
            className="border rounded-sm p-4 text-left transition-all hover:bg-white/[0.02]"
            style={{ borderColor: filter === verdict ? meta.color : "#1E1E24", background: filter === verdict ? `${meta.color}10` : "#0A0A0C" }}>
            <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>{verdict}</div>
            <div className="font-mono text-3xl font-medium text-white mt-1">{counts[verdict]}</div>
          </button>
        ))}
      </div>

      {sorted.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucun candidat. Lance le premier quiz dans l'onglet "Lancer un quiz".</div>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(c => {
            const meta = VERDICT_META[c.verdict] || {};
            return (
              <div key={c.id} className="border border-[#1E1E24] hover:border-white/20 bg-[#0A0A0C] rounded-sm p-4 flex items-center gap-4" data-testid={`candidate-${c.id}`}>
                <div className="font-mono text-3xl font-medium w-16 text-center" style={{ color: meta.color }}>{c.score_pct}%</div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{c.full_name}</div>
                  <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                    {c.email && <span>{c.email} · </span>}
                    {c.country && <span>{c.country} · </span>}
                    {c.timezone && <span>{c.timezone} · </span>}
                    <span style={{ color: meta.color }}>{c.verdict}</span>
                  </div>
                </div>
                <div className="hidden md:flex gap-1">
                  {QUIZ_CATEGORIES.slice(0, 4).map(cat => {
                    const got = c.scores_by_category?.[cat.id] || 0;
                    const max = (QUIZ_QUESTIONS[cat.id] || []).length * 5;
                    const pct = max ? Math.round((got / max) * 100) : 0;
                    return (
                      <div key={cat.id} className="text-center w-12">
                        <div className="text-[9px] font-mono text-zinc-600 uppercase">{cat.label.split(" ")[0].slice(0, 4)}</div>
                        <div className="font-mono text-xs mt-0.5" style={{ color: cat.color }}>{pct}%</div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => del(c.id)} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 block mb-1">{label}</span>
    <input type={type} value={value} required={required} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none" />
  </label>
);
